import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { elementToImageData } from 'apps/sigma2-ui/src/components/ui/handle-download-image';
import { ExplainImageRequest } from 'libs/end-to-end/src/explain';
import { ExplainImageResult } from 'libs/end-to-end/src/explain';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useExplanationsEnabled } from 'apps/sigma2-ui/src/lib/hooks/useExplanationsEnabled';

export interface ExplainImageHook {
  /**
   * If the element's image is being explained
   */
  isExplaining: boolean;

  /**
   * If the explanation failed
   */
  explainFailed: boolean;

  /**
   * The last successful explanation of the image
   */
  explanation: string | null;

  /**
   * Start the explanation of the image
   */
  explain: () => void;
}

/**
 * Hook to explain an image
 *
 * @param ref - The ref of the element to explain
 * @param autoExplain - If the image should be explained automatically
 * @param key - The key to use for auto invalidation of the explanation
 */
export const useExplainImage = ({
  ref,
  autoExplain,
  key,
}: {
  ref: React.RefObject<HTMLElement | null>;
  autoExplain: boolean;
  key: string;
}): ExplainImageHook => {
  const { userAuthenticatedApiCall, myUser } = useUser();
  const [isExplaining, setIsExplaining] = useState(false);
  const [explainFailed, setExplainFailed] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const hasAutoloadedKey = useRef<string | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const explainKey = useRef<string | null>(null);
  const enabled = useExplanationsEnabled();

  const explain = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || isExplaining || !ref.current || !myUser) {
        return;
      }

      explainKey.current = key;
      setIsExplaining(true);
      setExplainFailed(false);
      setExplanation(null);

      try {
        const imageToDataResult = await elementToImageData({
          element: ref.current,
          includeEffects: false,
        });

        if (!isAlive()) {
          return;
        }

        if (imageToDataResult.resultType === 'err') {
          setExplainFailed(true);
          return;
        }

        const request: ExplainImageRequest = {
          dataUrl: imageToDataResult.value,
        };
        const explainResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/explain/image`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
          },
          responseSchema: ExplainImageResult,
        });

        const keyMismatch = key !== explainKey.current;

        if (!isAlive() || !explainResult || keyMismatch) {
          return;
        }

        if (explainResult.resultType === 'err') {
          setExplainFailed(true);
          return;
        }

        setIsExplaining(false);
        setExplanation(explainResult.value.explanation);
      } finally {
        if (key === explainKey.current && isAlive()) {
          setIsExplaining(false);
        }
      }
    },
    [key, isExplaining, myUser, ref, userAuthenticatedApiCall],
  );

  useEffect(() => {
    if (
      enabled &&
      autoExplain &&
      hasAutoloadedKey.current !== key &&
      !isExplaining &&
      ref.current &&
      myUser
    ) {
      hasAutoloadedKey.current = key;

      // Let the browser render the image before explaining, but make sure to set these states for fast feedback.
      setExplainFailed(false);
      setExplanation(null);

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = null;
        explain();
      }, 250);
    }
  }, [key, enabled, autoExplain, explain, isExplaining, myUser, ref]);

  return useMemo(() => {
    if (!enabled) {
      return {
        isExplaining: false,
        explainFailed: false,
        explanation: null,
        explain: () => {},
      };
    }

    return {
      isExplaining,
      explainFailed,
      explanation,
      explain,
    };
  }, [enabled, explain, explainFailed, explanation, isExplaining]);
};
